/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

/* eslint-disable array-callback-return */
import React, { useState } from 'react';
import PropTypes from 'prop-types';

// Material UI
import InfoIcon from '@material-ui/icons/Info';
import CancelIcon from '@material-ui/icons/Cancel';

// Helpers
import { formatPrice } from '../../../utils/formater';
import { isEmpty } from '../../../utils/object';
import RegulatoryFeeInfo from './RegulatoryFeeInfo';


const updateKeyPosition = (obj, position) => {
    const keys = Object.keys(obj);
    const lastKey = keys.pop(); // Remove the last key
    keys.splice(position, 0, lastKey); // Insert the last key at the 3rd index

    // Create a new object with the rearranged keys
    const newObj = {};
    keys.forEach(key => {
        newObj[key] = obj[key];
    });

    return newObj;
}

const OrderSummaryHelper = ({ ROSSOrder, featureFlags, deviceType }) => {

    const [showRegulatoryFeeInfo, setShowRegulatoryFeeInfo] = useState(false);
    const isLegacyVCOrder = ROSSOrder.vtcorder;
    const isPassportOrder = ROSSOrder?.orders?.some((order) => order?.passportStatus === 'P');
    const isHidingOrderBreakdown = featureFlags['is-hiding-order-breakdown'] || false;
    const isFeeBreakdownEnabled = featureFlags['is-fee-breakdown-enabled'] || false;
    const isFECalculatingSubTotal = featureFlags?.['is-frontend-calculating-subtotal'] || false
    const isDisplayingReservebarTip = featureFlags['is-order-details-displaying-reservebar-tip'];
    const gcCardAmount = (giftCardPayments) => (giftCardPayments?.giftCardPaymet || []).reduce((totalAmount, { giftCardAmount }) => +giftCardAmount + totalAmount, 0);

    let orderSummary = {
        subTotal: { label: 'Merchandise:', price: 0 },
        promotionDiscountAmount: { label: 'Promotion:', price: 0 },
        shippingCharge: { label: isPassportOrder ? 'Passport Shipping:' : 'Shipping Fee:', price: 0 },
        serviceCharge: { label: isPassportOrder ? 'Passport Service:' : 'Service Fee:', price: 0 },
        regulatoryFees: { label: 'Regulatory Fee:', price: 0 },
        deliveryFee: { label: 'Delivery Fee:', price: 0 },
        tip: { label: 'Tip:', price: 0 },
        totalBeforeTax: { label: 'Total Before Tax:', price: 0 },
        tax: { label: 'Taxes:', price: 0 },
        giftCardAmount: { label: 'Gift Card:', price: 0 },
        totalCharge: { label: 'Order Total:', price: 0 },
    };

    const regulatoryFeesForTooltip = {
        bottleFee: 0,
        deliveryFee: 0,
    };
    /*
     * IMPORTANT!!!!
     * 
     * Colorado Delivery Fee is double counted in the shippingCharge
     * 
     * Bottle Fee is double counted in the tax
     * 
     */

    const hideShippingCharge = !isEmpty(ROSSOrder) && ROSSOrder.orders?.length > 0 &&
    ROSSOrder.orders.some(order => 
        order?.billingProfile?.addressDetail?.state?.toLowerCase() === 'ca' && 
        order.items?.some(item => item.shippingItemChargeTypes?.length > 0)
    );

    if (!isEmpty(ROSSOrder)) {
        const isOMSOrder = ROSSOrder?.orders?.some((order) => order.omsOrderNumber);
        ROSSOrder.orders.forEach((billing, ind) => {
            const hasReservebarTip = ROSSOrder.orders[ind]?.billingProfile?.tip;
            const isCABilling = ROSSOrder.orders[ind]?.billingProfile?.addressDetail?.state.toLowerCase() === 'ca';
            const itemDetailList = ROSSOrder.orders[ind].items;
            const billDetail = ROSSOrder.orders[ind].billingProfile;
            const DCHFee = (isFeeBreakdownEnabled && itemDetailList[0].itemType?.toLowerCase() === 'fpt') ? billDetail?.distCharges?.charges.find((_) => _.distChrgType === 'DCH') ?? null : null;
            const BCHFee = billDetail?.distCharges?.charges.find((_) => _.distChrgType === 'BCH');
            orderSummary.promotionDiscountAmount.price += Number(billDetail?.promotionDiscountAmount || 0);
            
            orderSummary.tax.price += Number(billDetail?.tax || 0);
            orderSummary.giftCardAmount.price += Number(gcCardAmount(ROSSOrder.orders[ind]?.billingProfile?.giftCardPaymets));
            orderSummary.totalCharge.price += Number(billDetail?.totalCharge || 0);
            orderSummary.regulatoryFees.price += Number(billDetail?.deliveryFee || 0);
            orderSummary.regulatoryFees.price += Number(billDetail?.bottleFee || 0);
            orderSummary.deliveryFee.price += Number(DCHFee?.distAmt || 0);

            regulatoryFeesForTooltip.deliveryFee += Number(billDetail?.deliveryFee || 0);
            regulatoryFeesForTooltip.bottleFee += Number(billDetail?.bottleFee || 0);

            // adding gift card price to total before tax
            orderSummary.totalBeforeTax.price += (Number(billDetail?.totalCharge || 0) - Number(billDetail?.tax || 0) + Number(gcCardAmount(ROSSOrder.orders[ind]?.billingProfile?.giftCardPaymets)));
            // Only reservebar orders have the tip field, so first we need to check if the field exists
            if (hasReservebarTip && isDisplayingReservebarTip) orderSummary.tip.price += Number(billDetail?.tip || 0)

            if (!isCABilling || itemDetailList.every((item) => !item.shippingItemChargeTypes)) {
                if (!DCHFee && itemDetailList[0]?.itemType?.toLowerCase() === 'fpt') {
                    orderSummary.serviceCharge.price += Number(billDetail?.shippingCharge || 0);
                    orderSummary.serviceCharge.price += Number(billDetail?.surcharge || 0);
                } else if (itemDetailList[0]?.itemType?.toLowerCase() === 'gpt') {
                    
                    orderSummary.shippingCharge.price += Number(billDetail?.shippingCharge || 0);
                    orderSummary.shippingCharge.price += Number(billDetail?.surcharge || 0);
                }
            }

            itemDetailList.forEach((item) => {
                if (isFECalculatingSubTotal) {
                    let totalItemSubtotal = Number(item.price) * Number(item.quantity)
                    orderSummary.subTotal.price += totalItemSubtotal
                    if (DCHFee) orderSummary.subTotal.price += Number(BCHFee?.distAmt || 0);
                } else {
                    orderSummary.subTotal.price += Number(billDetail?.orderSubTotal || 0);
                }
                
                if (isCABilling && item.shippingItemChargeTypes?.length > 0) {
                    item.shippingItemChargeTypes.forEach((chargeType) => {
                        if(item.itemType?.toLowerCase() === 'fpt') {
                            if (chargeType.chargeTypeCode?.toLowerCase() === 'sch' && chargeType.description?.length > 0 && Number(chargeType.amount) > 0) {
                                const key = chargeType.description.replace(' ', '');
                                orderSummary[key] = {label: `${chargeType.description}:`, price: (orderSummary[key]?.price || 0) + Number(chargeType.amount)};
                                orderSummary = updateKeyPosition(orderSummary, 4);
                            }
                            if (chargeType.chargeTypeCode?.toLowerCase() === 'bch' && Number(chargeType.amount) > 0) {
                                orderSummary.serviceCharge.price += Number(chargeType.amount);
                            }
                        }
                        else if(item.itemType?.toLowerCase() === 'gpt') {
                            if (chargeType.chargeTypeCode?.toLowerCase() === 'bch' && Number(chargeType.amount) > 0) {
                                orderSummary.shippingCharge.price += Number(chargeType.amount);
                            }
                        }
                    })
                }
            });
        });
        // at the end of the loop, deduct all the regulatory fees from shipping
        // excluding deduction for OMS order
        if (!isOMSOrder && regulatoryFeesForTooltip.deliveryFee > 0) {
            if (orderSummary.serviceCharge.price > 0) orderSummary.serviceCharge.price -= regulatoryFeesForTooltip.deliveryFee;
            if (orderSummary.shippingCharge.price > 0) orderSummary.shippingCharge.price -= regulatoryFeesForTooltip.deliveryFee;
        }
    }

    return (
        <div className="summary-info margin-bottom">
            {Object.keys(orderSummary).map((key) => {
                if (isHidingOrderBreakdown && isLegacyVCOrder && key !== 'totalCharge') return '';
                if (key === 'promotionDiscountAmount' && orderSummary[key].price === 0) return '';
                if (key === 'giftCardAmount' && orderSummary[key].price === 0) return '';
                if (key === 'tip' && orderSummary[key].price === 0) return '';
                if (hideShippingCharge && key === 'shippingCharge' && orderSummary[key].price === 0) return '';
                if (hideShippingCharge && key === 'serviceCharge' && orderSummary[key].price === 0) return '';
                if (key === 'deliveryFee' && orderSummary[key].price === 0) return '';
                if (
                    (key === 'promotionDiscountAmount' || key === 'giftCardAmount') && orderSummary[key].price === 0
                ) {
                    return '';
                }
                return (
                    <React.Fragment key={key}>
                        <div className="split">
                            <div className="summary-label">
                                {orderSummary[key].label}
                                {
                                    key === 'regulatoryFees' && orderSummary.regulatoryFees.price > 0 ? (
                                        showRegulatoryFeeInfo ? (
                                            <CancelIcon style={{ fontSize: '1rem', cursor: 'pointer' }} onClick={() => setShowRegulatoryFeeInfo(false)} />
                                        ) : <InfoIcon style={{ fontSize: '1rem', cursor: 'pointer' }} onClick={() => setShowRegulatoryFeeInfo(true)} />
                                    ) : null
                                }
                            </div>
                            <div 
                                className={`
                                    summary-price 
                                    ${['promotionDiscountAmount'].includes(key) ? 'green-font' : ''}
                                    ${['totalBeforeTax', 'totalCharge'].includes(key) ? 'bold-font' : ''}
                                `} 
                            >
                                {key === 'promotionDiscountAmount'
                                    || key === 'giftCardAmount'
                                    ? '-'
                                    : ''}
                                ${formatPrice(orderSummary[key].price)}
                            </div>
                        </div>
                        {
                            key === 'regulatoryFees'
                                && showRegulatoryFeeInfo
                                ? <RegulatoryFeeInfo
                                    deviceType={deviceType}
                                    regulatoryFeesForTooltip={regulatoryFeesForTooltip}
                                    setShowRegulatoryFeeInfo={setShowRegulatoryFeeInfo}
                                />
                                : null
                        }
                    </React.Fragment>
                );
            })}
        </div >
    );
}

OrderSummaryHelper.defaultProps = {
    ROSSOrder: {},
    featureFlags: {},
};

OrderSummaryHelper.propTypes = {
    ROSSOrder: PropTypes.shape({
        orders: PropTypes.arrayOf(PropTypes.shape({
            billingProfile: PropTypes.object.isRequired,
        })),
    }),
    featureFlags: PropTypes.object.isRequired
};

export default OrderSummaryHelper;